import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { HOW_IT_WORKS_SECTIONS } from '../../constants/helpContent';
import styles from './Support.module.css';

// ─── Extra FAQ items that live only on the support page ──────────────────────
// These are Apple-required items (account deletion + bug reports) that are
// deliberately NOT in HOW_IT_WORKS_SECTIONS (they are not "how it works" help).
const SUPPORT_ONLY_SECTIONS = [
    {
        id: 'delete-account',
        emoji: '🗑️',
        anchor: 'delete-account',
        title: 'How do I delete my account or request my data?',
        body: [
            'You can delete your Aluvo account at any time from the app\'s Settings screen.',
            'Go to Profile → Settings → Delete Account. You will be asked to confirm twice before anything is removed.',
            'After confirmation, your personal data (profile, coins, challenge history) is permanently deleted within 30 days.',
            'To request a copy of your data or to ask us to delete your data by email, write to privacy@aluvo.app.',
        ],
        footer: null,
        highlight: null,
    },
    {
        id: 'bug-report',
        emoji: '🐛',
        anchor: 'bug-report',
        title: 'I found a bug or an incorrect challenge question — how do I report it?',
        body: [
            'You can report question errors directly inside the app — there is a "Report question" link on every challenge screen.',
            'For bugs, crashes, or anything else unexpected, email us at support@aluvo.app with a short description of what happened and, if possible, a screenshot.',
            'We read every report and act on them quickly — thank you for helping us improve.',
        ],
        footer: null,
        highlight: null,
    },
];

// ─── Accordion ────────────────────────────────────────────────────────────────
function AccordionItem({ section, defaultOpen = false }) {
    const [open, setOpen] = useState(defaultOpen);
    const itemRef = useRef(null);

    return (
        <div className={styles.accordion} id={section.anchor} ref={itemRef}>
            <button
                className={styles.accordionHeader}
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
            >
                <span className={styles.accordionEmoji}>{section.emoji}</span>
                <span className={styles.accordionTitle}>{section.title}</span>
                <span className={`${styles.accordionChevron} ${open ? styles.chevronOpen : ''}`}>›</span>
            </button>

            {open && (
                <div className={styles.accordionBody}>
                    {section.body?.map((line, i) =>
                        line === '' ? (
                            <div key={i} className={styles.spacer} />
                        ) : (
                            <p key={i} className={styles.bodyText}>{line}</p>
                        ),
                    )}

                    {/* Tier table (Coin Tiers section) */}
                    {section.tiers && (
                        <div className={styles.table}>
                            {section.tiers.map((tier) => (
                                <div key={tier.label} className={styles.tableRow}>
                                    <span className={styles.tierEmoji}>{tier.emoji}</span>
                                    <div className={styles.tierInfo}>
                                        <span className={styles.tierLabel}>{tier.label}</span>
                                        <span className={styles.tierDesc}>{tier.description}</span>
                                    </div>
                                    <span className={styles.tierScore}>{tier.score}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Series Mastery table */}
                    {section.masteryTable && (
                        <div className={styles.table}>
                            {section.masteryTable.map((row) => (
                                <div key={row.range} className={styles.tableRow}>
                                    <span className={styles.masteryRange}>{row.range}</span>
                                    <span className={styles.masteryRequired}>{row.required}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Legendary highlight */}
                    {section.highlight && (
                        <div className={styles.highlight}>
                            <p className={styles.highlightText}>{section.highlight}</p>
                        </div>
                    )}

                    {/* Footer note */}
                    {section.footer && (
                        <p className={styles.footerNote}>{section.footer}</p>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function Support() {
    // Combine in-app help sections with support-only items
    const allSections = [...HOW_IT_WORKS_SECTIONS, ...SUPPORT_ONLY_SECTIONS];

    return (
        <div className={styles.page}>
            <div className={styles.container}>

                {/* ── Hero ── */}
                <div className={styles.hero}>
                    <h1 className={styles.heroTitle}>Need help with Aluvo? We're here.</h1>
                    <p className={styles.heroSubline}>Find answers below, or reach out directly.</p>
                </div>

                {/* ── FAQ accordion ── */}
                <section className={styles.faqSection}>
                    <h2 className={styles.sectionHeading}>Frequently Asked Questions</h2>
                    <div className={styles.sections}>
                        {allSections.map((section) => (
                            <AccordionItem key={section.id} section={section} />
                        ))}
                    </div>
                    <p className={styles.versionNote}>Rules last updated June 2026</p>
                </section>

                {/* ── Contact ── */}
                <section className={styles.contactSection}>
                    <div className={styles.contactCard}>
                        <span className={styles.contactIcon}>✉️</span>
                        <h2 className={styles.contactTitle}>Still need help?</h2>
                        <p className={styles.contactBody}>
                            Email us at{' '}
                            <a href="mailto:support@aluvo.app" className={styles.emailLink}>
                                support@aluvo.app
                            </a>{' '}
                            and we'll get back to you as soon as we can.
                        </p>
                    </div>
                </section>

                {/* ── Footer links (Apple-required) ── */}
                <footer className={styles.pageFooter}>
                    <div className={styles.footerLinks}>
                        <Link to="/privacy" className={styles.footerLink}>Privacy Policy</Link>
                        <span className={styles.footerDot}>·</span>
                        <Link to="/terms" className={styles.footerLink}>Terms of Service</Link>
                        <span className={styles.footerDot}>·</span>
                        <a href="#delete-account" className={styles.footerLink}>Account Deletion</a>
                    </div>
                    <p className={styles.footerCopy}>© {new Date().getFullYear()} Aluvo. All rights reserved.</p>
                </footer>

            </div>
        </div>
    );
}